import { ChevronRight, Heart, Instagram, MapPin, Phone } from 'lucide-react';
import { Link, NavLink } from 'react-router';

import { CONTACT, SITE } from '@/config/site';
import { CATEGORIES } from '@/data/categories';
import { NAV_LINKS } from '@/data/navigation';
import { useFavorites } from '@/features/favorites/favorites.context';
import { cn } from '@/lib/cn';
import { WA_LINKS } from '@/lib/whatsapp';

import { WhatsAppIcon } from '../common/icons';
import { Logo } from '../common/Logo';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Dialog, DialogTitle, SheetContent } from '../ui/dialog';
import { Separator } from '../ui/misc';

interface MobileMenuProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MobileMenu({ open, onOpenChange }: MobileMenuProps) {
  const { count: favoriteCount } = useFavorites();
  const close = () => onOpenChange(false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <SheetContent side="left" aria-describedby={undefined} className="lg:hidden">
        <header className="flex items-center border-b border-sand-300 px-5 py-4 pr-14">
          <DialogTitle className="sr-only">Menu de navegação</DialogTitle>
          <Logo static className="h-12" />
        </header>

        <nav aria-label="Menu" className="flex-1 overflow-y-auto px-5 py-5">
          <ul className="flex flex-col gap-1">
            {NAV_LINKS.map((link) => (
              <li key={link.to}>
                <NavLink
                  to={link.to}
                  onClick={close}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center justify-between rounded-xl px-4 py-3.5 font-display text-base font-bold transition-colors',
                      isActive && link.to !== '/'
                        ? 'bg-cream-300 text-orange-600'
                        : 'text-brown-800 hover:bg-cream-200',
                    )
                  }
                >
                  {link.label}
                  <ChevronRight className="size-4 text-caramel-500" aria-hidden="true" />
                </NavLink>
              </li>
            ))}

            <li>
              <Link
                to="/favoritos"
                onClick={close}
                className="flex items-center justify-between rounded-xl px-4 py-3.5 font-display text-base font-bold text-brown-800 transition-colors hover:bg-cream-200"
              >
                <span className="flex items-center gap-2">
                  <Heart className="size-4 text-orange-500" aria-hidden="true" />
                  Favoritos
                </span>
                {favoriteCount > 0 && (
                  <Badge variant="primary" size="sm">
                    {favoriteCount}
                  </Badge>
                )}
              </Link>
            </li>
          </ul>

          <Separator className="my-5" />

          <p className="mb-3 px-1 font-display text-xs font-bold tracking-wider text-brown-700 uppercase">
            Comprar por categoria
          </p>

          <ul className="grid grid-cols-2 gap-2">
            {CATEGORIES.map((category) => (
              <li key={category.id}>
                <Link
                  to={`/catalogo${category.query}`}
                  onClick={close}
                  className="flex h-full flex-col gap-1 rounded-2xl border-2 border-sand-300 bg-white p-3 transition-colors hover:border-caramel-400 hover:bg-cream-200"
                >
                  <span className="text-2xl" aria-hidden="true">
                    {category.emoji}
                  </span>
                  <span className="font-display text-xs leading-tight font-bold text-brown-900">
                    {category.title}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <footer className="flex flex-col gap-3 border-t border-sand-300 bg-cream-200 px-5 py-4">
          <Button asChild variant="whatsapp" size="md" full>
            <a href={WA_LINKS.general} target="_blank" rel="noopener noreferrer">
              <WhatsAppIcon aria-hidden="true" />
              Falar no WhatsApp
            </a>
          </Button>

          <div className="flex flex-col gap-1.5 text-xs text-brown-700">
            <a
              href={`tel:+${CONTACT.whatsappNumber}`}
              className="flex items-center gap-2 font-semibold"
            >
              <Phone className="size-3.5 text-caramel-500" aria-hidden="true" />
              {CONTACT.whatsappDisplay}
            </a>
            <a
              href={CONTACT.instagramUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 font-semibold"
            >
              <Instagram className="size-3.5 text-caramel-500" aria-hidden="true" />@
              {CONTACT.instagramHandle}
            </a>
            <p className="flex items-center gap-2">
              <MapPin className="size-3.5 text-caramel-500" aria-hidden="true" />
              {SITE.serviceArea}
            </p>
          </div>
        </footer>
      </SheetContent>
    </Dialog>
  );
}
